import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, Node } from '@contentful/rich-text-types';
import { DocumentWithEmbeddedEntries } from 'libs/contentful/types';
import { Document } from '@contentful/rich-text-types';
import { getCleanDocument } from 'utils/formatters';
import { Node as ContentfulNode } from '@contentful/rich-text-types';
import { PAGE_LINK_TYPENAME_SET } from 'libs/contentful/queries/page/types';
import Link from 'components/shared/Link/Link';
import renderEmbeddedEntry from 'components/nodes/EmbeddedEntry';
import { rewriteSubdomainSlug } from 'utils/enterprise';

import { Options as RichTextRendererOptions } from '@contentful/rich-text-react-renderer';

export interface RichTextInlineProps {
  inline: DocumentWithEmbeddedEntries;
  additionalOptions?: RichTextRendererOptions;
}

const RichTextInline = ({ inline, additionalOptions }: RichTextInlineProps) => {
  // Keep original headline for links logic
  const originalInline = inline;

  // Clean document for rendering
  const cleanHeadline = getCleanDocument(inline) as Document;

  const renderEntryLink = (node: ContentfulNode, children: React.ReactNode) => {
    const entryLink =
      'links' in originalInline &&
      originalInline.links?.entries?.hyperlink?.find(entry => entry.sys.id === node.data.target.sys.id);

    if (entryLink) {
      if (PAGE_LINK_TYPENAME_SET.has(entryLink.__typename) && entryLink.slug) {
        const slug = rewriteSubdomainSlug(entryLink.slug);

        return (
          <Link className="inline underline" data-testid="inline-hyperlink" href={`/${slug}`}>
            {children}
          </Link>
        );
      }
      if (entryLink.__typename === 'ExternalLink' && entryLink.url) {
        return (
          <Link className="inline underline" data-testid="inline-hyperlink" href={entryLink.url}>
            {children}
          </Link>
        );
      }
    }

    return <>{children}</>;
  };

  const renderInline = (_: Node, children: React.ReactNode) => {
    // Prevents trailing new lines in the Contentful field from rendering an empty heading
    if (children?.toString().trim() === '') return null;

    return <span data-testid="inline-richtext">{children}</span>;
  };

  return documentToReactComponents(cleanHeadline, {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => renderInline(node, children),
      [INLINES.ENTRY_HYPERLINK]: (node, children) => renderEntryLink(node, children),
      [INLINES.HYPERLINK]: (node, children) => (
        <Link href={node.data.uri} className="inline underline" data-testid="inline-hyperlink">
          {children}
        </Link>
      ),
      [INLINES.EMBEDDED_ENTRY]: (node: Node, children: React.ReactNode) => renderEmbeddedEntry(node, children, inline),
    },
    ...additionalOptions,
  });
};

export default RichTextInline;
